import React, { useState, useEffect, useMemo } from 'react'
import Calendar from 'react-calendar'
import moment from 'moment'
import { EditorState, convertToRaw } from 'draft-js'
import 'react-calendar/dist/Calendar.css'
import { isEmpty, toNumber } from 'lodash'
import {
  AdvisorHeading,
  AdvisorTitle,
  AdvisorWrapper,
  CalendarTitle,
  CalendarWrapper,
  IconWithLabel,
  Divider,
  InputFieldsWrapper,
  MeetingWrapper,
  NoSlotLabel,
  SelectDateWrapper,
  SelectedDate,
  RequestMeetingWrapper,
  StartTimeLabel,
  TimeDuration,
  DurationWrapper,
  CheckboxWrapper,
} from 'components/Calendar/style'
import { ACTION_TYPE, FORM, MEETING_STEPPER, ROLES, USER_ROLES, availabilityStatus } from 'config'
import ClockIcon from 'components/Common/SvgIcons/ClockIcon'
import CalendarIcon from 'components/Common/SvgIcons/CalendarIcon'
import {
  checkAvailability,
  getRoleFromPath,
  getUserRole,
  requestMeetingLogsFields,
} from 'utils/helper'
import { addDays } from 'date-fns'

import draftToHtml from 'draftjs-to-html'
import hookForms from 'utils/hookForms'

import CircleNotchIcon from '../../Common/SvgIcons/CircleNotchIcon'
import SlotsSelector from '../SlotsSelector'
import PaginatedSlots from '../PaginatedSlots'
import { ampli } from 'ampli'
import MeetingDetails from '../Meetings/MeetingDetails'
import {
  CalendarEvent,
  GetUserAvailability,
  UserProfile,
} from '../../../__generated__/api-types-and-hooks'
import { Controller, useForm } from 'react-hook-form'
import CheckBoxField from 'components/Common/CheckBoxField'
import GradientCircleNotchIcon from 'components/Common/SvgIcons/GradientCircleNotchIcon'
import { getTimeZoneDisplayRepresentation } from 'utils/dates'
import useFormContainer from 'hooks/useFormContainer'
import { yupResolver } from '@hookform/resolvers/yup'
import { meetingsSchema } from '../Meetings/validations'

export type ISelectedClient = UserProfile & {
  businessName: string
}
interface IMeetingScheduleProps {
  user: UserProfile
  boAdvisors: UserProfile[]
  isDisabled: boolean
  meetingStepper: MEETING_STEPPER
  setShowModalBox: (showModalBox: boolean) => void
  organizationName: string
  selectedAdvisorId?: string | null
  setUserMeetingAction: (calendarEvent: CalendarEvent) => void
  updateMeetingStepperAction: (input: { actionType: string; stepName: MEETING_STEPPER }) => void
  advisorProfile: UserProfile
  selectedClient?: ISelectedClient | null
  setSelectedClient: (client?: ISelectedClient | null) => void
  selectedDuration: number
  getUserAvailabilityAction
  userAvailabilityList: GetUserAvailability
  userAvailabilityStatus
  userAvailableMeetingLocation: { label: string; value: string }[]
}

const MeetingSchedule = ({
  user,
  boAdvisors,
  isDisabled,
  meetingStepper,
  setShowModalBox,
  organizationName,
  selectedAdvisorId,
  userAvailabilityList,
  setUserMeetingAction,
  updateMeetingStepperAction,
  getUserAvailabilityAction,
  advisorProfile,
  userAvailabilityStatus,
  selectedClient,
  selectedDuration,
  setSelectedClient,
  userAvailableMeetingLocation,
}: IMeetingScheduleProps) => {
  let timeZone = user?.timezone ?? moment.tz.guess()
  const currentTime = timeZone
    ? moment(new Date()).tz(timeZone).format('hh:mm a')
    : moment().format('hh:mm a')
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedSlot, setSelectedSlot] = useState({
    slotStartTime: null,
    slotEndTime: null,
    slotStartDate: null,
    slotEndDate: null,
  })
  const userRole = getRoleFromPath()
  const [availableSlots, setAvailableSlots]: any = useState([])
  const [calendarDate, setCalendarDate] = useState(new Date())
  const [meetingDescription, setMeetingDescription] = useState(EditorState.createEmpty())
  const [startDateTime, setStartDateTime] = useState(new Date())

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(meetingsSchema),
  })

  const { control } = methods

  const form = hookForms.getForm(FORM.MEETING_SCHEDULED)
  const formData = form?.getValues()

  const selectedAdvisor = useMemo(() => {
    return boAdvisors.find((advisor) => advisor.id === selectedAdvisorId)
  }, [boAdvisors, selectedAdvisorId])

  useEffect(() => {
    if (selectedDate && userAvailabilityList?.availability) {
      const availability = userAvailabilityList.availability.find(
        (slot) => slot?.date === selectedDate
      )

      setAvailableSlots(availability?.slots || [])
    }
  }, [selectedDate, userAvailabilityList])

  const isBusinessOwner =
    getRoleFromPath() === ROLES.BUSINESS_OWNER || getRoleFromPath() === ROLES.V2
  const participant = isBusinessOwner ? selectedAdvisor : selectedClient
  const participantLabel = isBusinessOwner ? 'Advisor' : 'Client'
  const onSubmit = () => {
    const form = hookForms.getForm(FORM.MEETING_SCHEDULED)

    const meetingForm = form?.getValues()
    methods.reset()
    const extraAttendeesEmails = meetingForm?.attendees
      ? meetingForm?.attendees.split(',').map((attendee) => attendee.trim())
      : []

    let description =
      convertToRaw(meetingDescription.getCurrentContent()).blocks[0].text.length > 0
        ? draftToHtml(convertToRaw(meetingDescription.getCurrentContent()))
        : ''

    let availabilityHours = {
      description,
      title: meetingForm?.meetingTitle,
      meetingLocationTool: meetingForm?.meetingLocationTool,
      attendees: [...extraAttendeesEmails],
      role: [getUserRole()],
      startTime: selectedSlot ? selectedSlot.slotStartDate : null,
      endTime: selectedSlot ? selectedSlot.slotEndDate : null,
      participantId: participant?.id,
      timezone: user?.timezone,
      location: meetingForm?.meetingLocation,
      videoLinkDescription: meetingForm?.videoLinkDescription,
      organizerName: user?.firstName + ' ' + user?.lastName,
      participantName: participant?.firstName + ' ' + participant?.lastName,
      businessName: selectedClient?.businessName || '',
      organizationName,
      duration: userAvailabilityList.duration || '',
      ignoreConflicts: formData?.ignoreConflicts ?? false,
      teamMemberList: meetingForm?.teamMemberList,
    }
    setUserMeetingAction(availabilityHours)
    setSelectedClient(null)

    ampli.meetingRequested({
      initiatorRole: userRole,
      meetingTitle: availabilityHours.title,
      originatorName: availabilityHours.organizerName,
      guests: availabilityHours.attendees,
      duration: userAvailabilityList?.duration || '',
      ...requestMeetingLogsFields(
        availabilityHours?.participantName,
        selectedClient?.businessName || '',
        organizationName || ''
      ),
    })
  }
  const handleCalendarChange = (date) => {
    setCalendarDate(date)
    setSelectedDate(moment(date).local().format('YYYY-MM-DD'))
  }

  const handleTileClick = (date) => {
    updateMeetingStepperAction({ actionType: 'increment', stepName: MEETING_STEPPER.SLOTS })
    setSelectedDate(moment(date).local().format('YYYY-MM-DD'))
  }
  const refreshAdvisorAvailability = ({ action, activeStartDate, value, view }) => {
    if (action !== ACTION_TYPE.OnChange) {
      const currentUserRole = getUserRole()
      const duration =
        currentUserRole === USER_ROLES.BUSINESS_OWNER
          ? userAvailabilityList.duration
          : selectedDuration
      const isStartDatePast = moment(activeStartDate).isBefore(moment())
      const startTime = isStartDatePast
        ? moment().toISOString()
        : moment(activeStartDate).toISOString()
      const endTime = isStartDatePast
        ? moment().add(50, 'days').toISOString()
        : moment(activeStartDate).add(50, 'days').toISOString()
      getUserAvailabilityAction({
        role: [currentUserRole],
        startTime,
        endTime,
        participantId: participant?.id,
        timeZone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        duration,
        ignoreConflicts: formData?.ignoreConflicts ?? false,
      })
    }
    setCalendarDate(activeStartDate)
    setStartDateTime(activeStartDate)
  }

  function getTileClassName({ date, view, userAvailabilityList }) {
    if (view === 'month' && userAvailabilityList) {
      if (checkAvailability(date, userAvailabilityList)) {
        return 'highlight'
      }
    }
    return ''
  }
  let dateObj = moment(selectedDate, 'YYYY-MM-DD')
  let formattedDate = dateObj.format('dddd, MMMM D, YYYY')

  const userRoles = useMemo(() => user.roles, [user])

  const handleChange = (event) => {
    getUserAvailabilityAction({
      role: [getUserRole()],
      startTime: startDateTime.toISOString(),
      endTime: addDays(startDateTime, 31).toISOString(),
      participantId: userRoles?.includes(USER_ROLES.BUSINESS_OWNER)
        ? selectedAdvisorId
        : selectedClient && selectedClient.id,
      timeZone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      duration: toNumber(selectedDuration),
      ignoreConflicts: event.target.checked,
    })
  }

  useFormContainer(FORM.MEETING_SCHEDULED, methods)

  return (
    <MeetingWrapper>
      <SelectDateWrapper>
        {meetingStepper === MEETING_STEPPER.CALENDAR && (
          <CalendarWrapper>
            <AdvisorWrapper>
              <AdvisorTitle>{participantLabel}</AdvisorTitle>
              <AdvisorHeading>
                {participant?.firstName} {participant?.lastName}
              </AdvisorHeading>
            </AdvisorWrapper>
            <DurationWrapper>{selectedDuration} min meeting</DurationWrapper>
            {
              // userRoles &&
              //   (userRoles.includes(USER_ROLES.BUSINESS_OWNER) ||
              //     userRoles.includes(USER_ROLES.BSO_ADVISOR)) &&
              !isBusinessOwner && (
                <CheckboxWrapper>
                  <Controller
                    name="ignoreConflicts"
                    control={control}
                    render={({ field }) => (
                      <CheckBoxField
                        {...field}
                        id=""
                        className="!gap-2 !pr-0"
                        onChange={(value) => {
                          field.onChange(value)
                          handleChange(value)
                        }}
                      />
                    )}
                  />
                  <span className="text">
                    {userRole === ROLES.BUSINESS_OWNER
                      ? 'Ignore scheduling conflicts with my calendar'
                      : 'Ignore scheduling conflicts'}{' '}
                  </span>
                </CheckboxWrapper>
              )
            }
            <CalendarTitle>Select Date:</CalendarTitle>
            {userAvailabilityStatus === availabilityStatus.LOADING ? (
              <div className="flex justify-center">
                <GradientCircleNotchIcon width="100" height="100" />
              </div>
            ) : (
              <Calendar
                view="month"
                showFixedNumberOfWeeks={true}
                formatDay={(_, date) => moment(date).local().format('DD')}
                ///formatShortWeekday={(_, date) => moment(date).utc().format('ddd')}
                formatShortWeekday={(_, date) => moment(date).local().format('ddd')}
                className="!w-full "
                onChange={handleCalendarChange}
                value={calendarDate}
                tileClassName={(props) => getTileClassName({ ...props, userAvailabilityList })}
                onClickDay={handleTileClick}
                locale="en-US"
                onActiveStartDateChange={refreshAdvisorAvailability}
              />
            )}
          </CalendarWrapper>
        )}
      </SelectDateWrapper>
      {meetingStepper === MEETING_STEPPER.SLOTS && userAvailabilityStatus === 'loading' && (
        <div className="col-span-12 flex flex-row justify-center p-12">
          <CircleNotchIcon className={'fill-primary'} width="63" height="66" />
        </div>
      )}
      {meetingStepper === MEETING_STEPPER.SLOTS &&
        isEmpty(availableSlots) &&
        userAvailabilityStatus !== 'loading' && (
          <NoSlotLabel>Sorry, No Slots Available on this Day</NoSlotLabel>
        )}
      {meetingStepper === MEETING_STEPPER.SLOTS && !isEmpty(availableSlots) && (
        <RequestMeetingWrapper>
          <SelectedDate>{formattedDate}</SelectedDate>
          <TimeDuration>
            <div className="mt-4">
              <p className="font-bold">Time Zone:</p>{' '}
              {getTimeZoneDisplayRepresentation(user?.timezone ?? moment.tz.guess())} ({currentTime}
              )
            </div>
          </TimeDuration>
          <TimeDuration>
            Meeting Duration: {`${userAvailabilityList?.duration} minutes`}{' '}
          </TimeDuration>

          <Divider />
          <StartTimeLabel>Select a start time:</StartTimeLabel>

          {isBusinessOwner ? (
            <SlotsSelector
              availableSlots={availableSlots}
              onClick={(slot) => {
                setSelectedSlot(slot)
                updateMeetingStepperAction({
                  actionType: 'increment',
                  stepName: MEETING_STEPPER.DESCRIPTION,
                })
              }}
            />
          ) : (
            <PaginatedSlots
              slots={availableSlots}
              date={selectedDate}
              onClick={(event) => {
                setSelectedSlot(event)
                updateMeetingStepperAction({
                  actionType: 'increment',
                  stepName: MEETING_STEPPER.DESCRIPTION,
                })
              }}
            />
          )}
        </RequestMeetingWrapper>
      )}

      {meetingStepper === MEETING_STEPPER.DESCRIPTION && (
        <RequestMeetingWrapper>
          <IconWithLabel>
            <CalendarIcon className={'inline mr-3 fill-primary-text'} />
            {formattedDate}
          </IconWithLabel>
          {selectedSlot && (
            <IconWithLabel>
              <ClockIcon
                className={'inline mr-3 fill-primary-disabled hover:!fill-primary-disabled'}
              />
              <div className="flex flex-col">
                {selectedSlot.slotStartTime} - {selectedSlot.slotEndTime}
                <p>({user.timezone})</p>
              </div>
            </IconWithLabel>
          )}
          <Divider />
          <InputFieldsWrapper>
            <MeetingDetails
              selectedUser={participant!}
              isEdit={false}
              onSubmit={onSubmit}
              isDisabled={isDisabled}
              description={meetingDescription}
              setDescription={setMeetingDescription}
              updateMeetingStepperAction={updateMeetingStepperAction}
              setShowModalBox={setShowModalBox}
              videoLinkDescription={
                advisorProfile?.defaultVideoLinkDescription ??
                user?.defaultVideoLinkDescription ??
                ''
              }
              user={user}
              userAvailableMeetingLocation={userAvailableMeetingLocation}
              methods={methods}
            />
          </InputFieldsWrapper>
        </RequestMeetingWrapper>
      )}
    </MeetingWrapper>
  )
}

export default MeetingSchedule
